import type {
  ApiSuccess, CreateVaultShareRequest, PaginationMeta,
  UpdateVaultFileRequest, VaultFileSummary, VaultShare, VaultStats, VaultTag,
  VaultTagLogEntry,
} from '@todo/shared';
import { api, del, get, getPaged, patch, post } from './client';

export interface VaultFilePage {
  rows: VaultFileSummary[];
  meta: PaginationMeta;
}

/** /api/folder - the file vault. Tables are vault_*; the module is Folder. */
export const vaultApi = {
  async list(params: {
    shelf?: 'mine' | 'shared' | 'org' | 'all';
    search?: string;
    ownerIds?: string[];
    tagIds?: string[];
    page?: number;
    pageSize?: number;
  } = {}): Promise<VaultFilePage> {
    const res = await getPaged<VaultFileSummary[]>('/folder/files', {
      shelf: params.shelf ?? 'all',
      ...(params.search ? { search: params.search } : {}),
      ...(params.ownerIds?.length ? { ownerIds: params.ownerIds.join(',') } : {}),
      ...(params.tagIds?.length ? { tagIds: params.tagIds.join(',') } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
    });
    return { rows: res.data, meta: res.meta as PaginationMeta };
  },

  stats(): Promise<VaultStats> {
    return get<VaultStats>('/folder/stats');
  },

  filters(): Promise<{
    owners: Array<{ id: string; displayName: string }>;
    tags: VaultTag[];
  }> {
    return get<{
      owners: Array<{ id: string; displayName: string }>;
      tags: VaultTag[];
    }>('/folder/filters');
  },

  /**
   * The file IS the request body. No multipart, no base64 - the same
   * carriage avatars and task attachments use, and the reason bytes come
   * through the API at all: the server can only magic-byte check what it
   * actually holds.
   */
  async upload(file: File, description?: string): Promise<VaultFileSummary> {
    const query = new URLSearchParams({ filename: file.name });
    if (description) query.set('description', description);
    const res = await api.post<ApiSuccess<VaultFileSummary>>(
      `/folder/files?${query.toString()}`,
      await file.arrayBuffer(),
      { headers: { 'Content-Type': file.type || 'application/octet-stream' } },
    );
    return res.data.data;
  },

  /**
   * A short-lived SAS URL plus the name to save it under. The browser then
   * fetches from Azure directly - the bytes never pass through the API.
   */
  downloadUrl(id: string): Promise<{ url: string; fileName: string }> {
    return get<{ url: string; fileName: string }>(`/folder/files/${id}/download`);
  },

  // ---- tags: the shared list, managed from Folder ----
  tags(): Promise<VaultTag[]> {
    return get<VaultTag[]>('/folder/tags');
  },

  createTag(name: string): Promise<VaultTag> {
    return api.post<ApiSuccess<VaultTag>>('/folder/tags', { name }).then((r) => r.data.data);
  },

  renameTag(id: string, name: string): Promise<VaultTag> {
    return api.put<ApiSuccess<VaultTag>>(`/folder/tags/${id}`, { name }).then((r) => r.data.data);
  },

  async removeTag(id: string): Promise<void> {
    await api.delete(`/folder/tags/${id}`);
  },

  tagLog(): Promise<VaultTagLogEntry[]> {
    return get<VaultTagLogEntry[]>('/folder/tag-log');
  },

  update(id: string, body: UpdateVaultFileRequest): Promise<VaultFileSummary> {
    return patch<VaultFileSummary>(`/folder/files/${id}`, body);
  },

  async remove(id: string): Promise<void> {
    await del(`/folder/files/${id}`);
  },

  shares(id: string): Promise<VaultShare[]> {
    return get<VaultShare[]>(`/folder/files/${id}/shares`);
  },

  share(id: string, body: CreateVaultShareRequest): Promise<VaultShare[]> {
    return post<VaultShare[]>(`/folder/files/${id}/shares`, body);
  },

  /** The API answers with the remaining grants, so this cannot use the
   *  shared del() helper, which discards the body. */
  async unshare(id: string, shareId: string): Promise<VaultShare[]> {
    const res = await api.delete<ApiSuccess<VaultShare[]>>(
      `/folder/files/${id}/shares/${shareId}`,
    );
    return res.data.data;
  },
};
